import { createContext, useContext, useState } from "react";

export class TableSelection {
    constructor(from, to) {
        this.from = from;
        this.to = to || from;
    }

    normalized() {
        return {
            minX: Math.min(this.from.cell, this.to.cell),
            maxX: Math.max(this.from.cell, this.to.cell),
            minY: Math.min(this.from.row, this.to.row),
            maxY: Math.max(this.from.row, this.to.row),
        };
    }

    contains(anchor, table) {
        const { minX, maxX, minY, maxY } = this.normalized();
        const { from, to } = table.getCellBoundaries(anchor.row, anchor.cell);
        return from >= minX && to <= maxX && anchor.row >= minY && anchor.row <= maxY;
    }

    bordersLeft(row, cell, table) {
        return this.normalized().minX === table.getCellBoundaries(row, cell).from;
    }

    bordersRight(row, cell, table) {
        return this.normalized().maxX === table.getCellBoundaries(row, cell).to;
    }

    bordersTop(row) {
        return this.normalized().minY === row;
    }

    bordersBottom(row) {
        return this.normalized().maxY === row;
    }

    selectRow(row, extend, table) {
        return new TableSelection(
            { row: extend ? this.from.row : row, cell: 0 },
            { row, cell: table.columns.length - 1 },
        );
    }

    selectColumn(column, extend, table) {
        return new TableSelection(
            { row: 0, cell: extend ? this.from.cell : column },
            { row: table.rows.length - 1, cell: column },
        );
    }

    isRowSelected(row, table) {
        const { minX, maxX, minY, maxY } = this.normalized();
        return row >= minY && row <= maxY && minX === 0 && maxX === table.columns.length - 1;
    }

    isAnyRowSelected(table) {
        for (let i = 0; i < table.rows.length; i += 1) {
            if (this.isRowSelected(i, table)) {
                return true;
            }
        }
        return false;
    }

    isColumnSelected(column, table) {
        const { minX, maxX, minY, maxY } = this.normalized();
        return column >= minX && column <= maxX && minY === 0 && maxY === table.rows.length - 1;
    }

    isAnyColumnSelected(table) {
        for (let i = 0; i < table.columns.length; i += 1) {
            if (this.isColumnSelected(i, table)) {
                return true;
            }
        }
        return false;
    }

    eq(other) {
        return (
            this.from.row === other.from.row &&
            this.from.cell === other.from.cell &&
            this.to.row === other.to.row &&
            this.to.cell === other.to.cell
        );
    }

    explode(table) {
        const expandOnce = (current) => {
            const { minX, maxX, minY, maxY } = current.normalized();
            for (let row = minY; row <= maxY; row += 1) {
                const start = table.getCellBoundaries(row, minX);
                const end = table.getCellBoundaries(row, maxX);
                if (start.from < minX) {
                    if (current.from.cell === minX) {
                        return new TableSelection(
                            { row: current.from.row, cell: start.from },
                            { row: current.to.row, cell: current.to.cell },
                        );
                    }
                    return new TableSelection(
                        { row: current.from.row, cell: current.from.cell },
                        { row: current.to.row, cell: start.from },
                    );
                }
                if (end.to > maxX) {
                    if (current.to.cell === maxX) {
                        return new TableSelection(
                            { row: current.from.row, cell: current.from.cell },
                            { row: current.to.row, cell: end.to },
                        );
                    }
                    return new TableSelection(
                        { row: current.from.row, cell: end.to },
                        { row: current.to.row, cell: current.to.cell },
                    );
                }
            }
            return current;
        };

        let last = this;
        for (let current = expandOnce(last); !current.eq(last); current = expandOnce(last)) {
            last = current;
        }
        return last;
    }

    moveRight(table) {
        const next = Math.min(table.columns.length - 1, table.getCellBoundaries(this.to.row, this.to.cell).to + 1);
        return new TableSelection({ row: this.to.row, cell: next }).explode(table);
    }

    moveLeft(table) {
        const next = Math.max(0, table.getCellBoundaries(this.to.row, this.to.cell).from - 1);
        return new TableSelection({ row: this.to.row, cell: next }).explode(table);
    }

    moveUp(table) {
        return new TableSelection({ row: Math.max(0, this.to.row - 1), cell: this.to.cell }).explode(table);
    }

    moveDown(table) {
        return new TableSelection({
            row: Math.min(table.rows.length - 1, this.to.row + 1),
            cell: this.to.cell,
        }).explode(table);
    }

    moveNext(table) {
        const bounds = table.getCellBoundaries(this.to.row, this.to.cell);
        if (bounds.to === table.columns.length - 1) {
            if (this.to.row === table.rows.length - 1) {
                return new TableSelection(this.to).explode(table);
            }
            return new TableSelection({ row: this.to.row + 1, cell: 0 }).explode(table);
        }
        return new TableSelection({ row: this.to.row, cell: bounds.to + 1 }).explode(table);
    }

    movePrevious(table) {
        const bounds = table.getCellBoundaries(this.to.row, this.to.cell);
        if (bounds.from === 0) {
            if (this.to.row === 0) {
                return new TableSelection(this.to).explode(table);
            }
            return new TableSelection({ row: this.to.row - 1, cell: table.columns.length - 1 }).explode(table);
        }
        return new TableSelection({ row: this.to.row, cell: bounds.from - 1 }).explode(table);
    }

    extendRight(table) {
        return new TableSelection(this.from, { row: this.to.row, cell: this.moveRight(table).to.cell }).explode(table);
    }

    extendLeft(table) {
        return new TableSelection(this.from, { row: this.to.row, cell: this.moveLeft(table).to.cell }).explode(table);
    }

    extendUp(table) {
        return new TableSelection(this.from, { row: Math.max(0, this.to.row - 1), cell: this.to.cell }).explode(table);
    }

    extendDown(table) {
        return new TableSelection(this.from, {
            row: Math.min(table.rows.length - 1, this.to.row + 1),
            cell: this.to.cell,
        }).explode(table);
    }

    spansEntireTable(table) {
        const { minX, maxX, minY, maxY } = this.normalized();
        return minX === 0 && maxX === table.columns.length - 1 && minY === 0 && maxY === table.rows.length - 1;
    }

    isMergedCellSelected(table) {
        if (this.from.row !== this.to.row) {
            return false;
        }
        const boundariesFrom = table.getCellBoundaries(this.from.row, this.from.cell);
        const boundariesTo = table.getCellBoundaries(this.to.row, this.to.cell);
        if (boundariesFrom.from !== boundariesTo.from) {
            return false;
        }
        const cellData = table.getCell(this.from.row, boundariesFrom.from);
        return cellData && Boolean(cellData.multiColumn);
    }

    isMergeableCells(table) {
        const { minX, maxX, minY, maxY } = this.normalized();
        if (minY !== maxY || minX === maxX) {
            return false;
        }
        for (let cell = minX; cell <= maxX; cell += 1) {
            if (table.getCell(this.from.row, cell).multiColumn) {
                return false;
            }
        }
        return true;
    }

    isOnlyFixedWidthColumns(table) {
        const { minX, maxX } = this.normalized();
        for (let cell = minX; cell <= maxX; cell += 1) {
            if (!this.isColumnSelected(cell, table) || !table.columns[cell].isParagraphColumn) {
                return false;
            }
        }
        return true;
    }

    isOnlyParagraphCells(table) {
        const { minX, maxX } = this.normalized();
        for (let cell = minX; cell <= maxX; cell += 1) {
            if (!table.columns[cell].isParagraphColumn) {
                return false;
            }
        }
        return true;
    }

    isOnlyNonFixedWidthColumns(table) {
        const { minX, maxX } = this.normalized();
        for (let cell = minX; cell <= maxX; cell += 1) {
            if (!this.isColumnSelected(cell, table) || table.columns[cell].isParagraphColumn) {
                return false;
            }
        }
        return true;
    }

    width() {
        const { minX, maxX } = this.normalized();
        return maxX - minX + 1;
    }

    height() {
        const { minY, maxY } = this.normalized();
        return maxY - minY + 1;
    }

    maximumCellWidth(table) {
        const { minX, maxX, minY, maxY } = this.normalized();
        let maxWidth = 1;
        for (let row = minY; row <= maxY; row += 1) {
            const start = table.getCellIndex(row, minX);
            const end = table.getCellIndex(row, maxX);
            maxWidth = Math.max(maxWidth, end - start + 1);
        }
        return maxWidth;
    }
}

const SelectionContext = createContext(undefined);

export function SelectionContextProvider({ children }) {
    const [selection, setSelection] = useState(null);
    const [dragging, setDragging] = useState(false);

    return (
        <SelectionContext.Provider value={{ selection, setSelection, dragging, setDragging }}>
            {children}
        </SelectionContext.Provider>
    );
}

export function useSelectionContext() {
    const context = useContext(SelectionContext);
    if (context === undefined) {
        throw new Error("useSelectionContext is only available inside SelectionContext.Provider");
    }
    return context;
}
